import { useState } from "react";
import { Wand2, Download } from "lucide-react";
import { toast } from "sonner";
import {
  exportHypeClip,
  listClipFormats,
  listClipTemplates,
  type ClipFormat,
  type ClipTemplate,
} from "@/lib/media/hypeClipComposer";
import { recordWarRoomEvent } from "@/lib/warRoom/warRoomEngine";
import { bumpMission } from "@/lib/ambassadors/ambassadorEngine";

const TEMPLATE_LABELS: Record<ClipTemplate, { label: string; head: string; sub: string }> = {
  nation_hype: { label: "Nation hype", head: "Tu nación domina el hype mundial", sub: "Únete · VerseSport World Cup" },
  goal_pressure: { label: "Goal pressure", head: "¡Tensión máxima al 89'!", sub: "Live drama · VerseSport" },
  cinematic_moment: { label: "Cinematic moment", head: "Momento cinematográfico en vivo", sub: "Estadio digital mundial" },
  world_status: { label: "World status", head: "Todo el planeta reacciona al mismo tiempo", sub: "World Status Live" },
  community_explosion: { label: "Community explosion", head: "La comunidad explota", sub: "Hype global ahora" },
};

export function HypeClipComposer() {
  const [template, setTemplate] = useState<ClipTemplate>("nation_hype");
  const [format, setFormat] = useState<ClipFormat>("vertical");
  const [headline, setHeadline] = useState(TEMPLATE_LABELS["nation_hype"].head);
  const [subline, setSubline] = useState(TEMPLATE_LABELS["nation_hype"].sub);
  const [busy, setBusy] = useState(false);

  const onTemplate = (t: ClipTemplate) => {
    setTemplate(t);
    setHeadline(TEMPLATE_LABELS[t].head);
    setSubline(TEMPLATE_LABELS[t].sub);
  };

  const onExport = async () => {
    setBusy(true);
    try {
      await exportHypeClip({ template, format, headline, subline });
      recordWarRoomEvent("clipExports");
      bumpMission("share_3_clips");
      toast.success("Clip listo para compartir");
    } catch {
      toast.error("No se pudo generar el clip");
    } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-border/60 bg-background/60 p-3">
      <h3 className="flex items-center gap-1.5 text-sm font-black uppercase tracking-wider">
        <Wand2 className="h-3.5 w-3.5 text-primary" /> Hype Clip Composer
      </h3>
      <div className="mt-2 grid gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Template</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {listClipTemplates().map((t) => (
              <button
                key={t}
                onClick={() => onTemplate(t)}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${
                  template === t ? "border-primary bg-primary/15 text-primary" : "border-border/60 bg-background/70"
                }`}
              >
                {TEMPLATE_LABELS[t].label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Formato</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {listClipFormats().map((f) => (
              <button
                key={f.id}
                onClick={() => setFormat(f.id)}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${
                  format === f.id ? "border-primary bg-primary/15 text-primary" : "border-border/60 bg-background/70"
                }`}
                title={`${f.w}×${f.h} · ${f.label}`}
              >
                {f.id} · {f.w}×{f.h}
              </button>
            ))}
          </div>
        </div>
        <label className="text-[11px] font-bold">
          Headline
          <input
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border/60 bg-background px-2 py-1 text-xs"
            maxLength={120}
          />
        </label>
        <label className="text-[11px] font-bold">
          Subline
          <input
            value={subline}
            onChange={(e) => setSubline(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border/60 bg-background px-2 py-1 text-xs"
            maxLength={140}
          />
        </label>
        <button
          onClick={onExport}
          disabled={busy}
          className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-xs font-black uppercase tracking-wider text-primary-foreground active:scale-95 disabled:opacity-60"
        >
          <Download className="h-3.5 w-3.5" /> Exportar clip
        </button>
      </div>
    </section>
  );
}